/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/pages/WorkflowDetailsPage.jsx
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Workflow Details
 *
 * PURPOSE:
 * Workflow Details & Workflow Step Management
 *
 ******************************************************************************/

import React, {
    useEffect,
    useState,
} from "react";

import { useParams } from "react-router-dom";

import WorkflowHeader from "../components/WorkflowHeader";
import WorkflowStepPanel from "../components/WorkflowStepPanel";
import WorkflowStepDrawer from "../components/WorkflowStepDrawer";

import useWorkflowMaster from "../hooks/useWorkflowMaster";
import useWorkflowStep from "../hooks/useWorkflowStep";

import expenseMasterService from "../../claims/services/expenseMasterService";

export default function WorkflowDetailsPage() {

    const { workflowId } = useParams();

    /**
     * ------------------------------------------------------------
     * Workflow Hook
     * ------------------------------------------------------------
     */

    const {

        loading: workflowLoading,

        workflow,

        loadWorkflow,

    } = useWorkflowMaster();

    /**
     * ------------------------------------------------------------
     * Workflow Step Hook
     * ------------------------------------------------------------
     */

    const {

        loading: stepLoading,

        saving,

        workflowSteps,

        loadWorkflowSteps,

        createWorkflowStep,

        updateWorkflowStep,

        deleteWorkflowStep,

        moveWorkflowStepUp,

        moveWorkflowStepDown,

        activateWorkflowStep,

        deactivateWorkflowStep,

    } = useWorkflowStep();

    /**
     * ------------------------------------------------------------
     * Drawer
     * ------------------------------------------------------------
     */

    const [drawerOpen, setDrawerOpen] =
        useState(false);

    const [drawerMode, setDrawerMode] =
        useState("create");

    const [selectedStep, setSelectedStep] =
        useState(null);

    /**
     * ------------------------------------------------------------
     * Initial Form
     * ------------------------------------------------------------
     */

    const initialFormData = {

        step_name: "",

        sequence_no: "",

        display_order: "",

        approval_level_id: "",

        approval_required: true,

        allow_delegate: false,

        allow_skip: false,

        auto_approve: false,

        escalation_after_hours: "",

        reject_action: "",

        reject_step_sequence: "",

        remarks: "",

        is_active: true,

    };

    const [formData, setFormData] =
        useState(initialFormData);

    const [errors, setErrors] =
        useState({});

    const [approvalLevels, setApprovalLevels] =
    useState([]);

    const [rejectActions, setRejectActions] =
        useState([]);    
    /**
     * ------------------------------------------------------------
     * Load Data
     * ------------------------------------------------------------
     */

    const loadData = async () => {

        const [

            approvalLevels,

            rejectActions

        ] = await Promise.all([

            expenseMasterService.listApprovalLevels(),

            expenseMasterService.listRejectActions()

        ]);
        console.log("Approval Levels:", approvalLevels);
        console.log("Reject Actions:", rejectActions);
        console.table(rejectActions);

        setApprovalLevels(approvalLevels);

        setRejectActions(rejectActions);

        await loadWorkflow(workflowId);

        await loadWorkflowSteps(workflowId);

    };

    useEffect(() => {

        loadData();

    }, [workflowId]);

    /**
     * ------------------------------------------------------------
     * Reset Form
     * ------------------------------------------------------------
     */

    const resetForm = () => {

        setFormData(initialFormData);

        setErrors({});

        setSelectedStep(null);

        setDrawerMode("create");

    };

    /**
     * ------------------------------------------------------------
     * Add Step
     * ------------------------------------------------------------
     */

    const handleAddStep = () => {

        resetForm();

        setDrawerOpen(true);

    };

    /**
     * ------------------------------------------------------------
     * Edit Step
     * ------------------------------------------------------------
     */

    const handleEditStep = (step) => {

        setDrawerMode("edit");

        setSelectedStep(step);

        setFormData({

            ...initialFormData,

            ...step,

        });

        setDrawerOpen(true);

    };

    /**
     * ------------------------------------------------------------
     * Drawer Close
     * ------------------------------------------------------------
     */

    const handleDrawerClose = () => {

        resetForm();

        setDrawerOpen(false);

    };

    /**
     * ------------------------------------------------------------
     * Form Change
     * ------------------------------------------------------------
     */

    const handleFormChange = (

        field,

        value

    ) => {

        setFormData((prev) => {

            const updated = {

                ...prev,

                [field]: value,

            };

            if (

                field === "reject_action" &&

                value !== "RETURN_TO_PREVIOUS_STEP"

            ) {

                updated.reject_step_sequence = "";

            }

            return updated;

        });

    };
        /**
     * ------------------------------------------------------------
     * Save Workflow Step
     * ------------------------------------------------------------
     */

    const handleSubmit = async (event) => {

        event.preventDefault();

        const validationErrors = {};

        if (!formData.step_name?.trim()) {

            validationErrors.step_name =
                "Step Name is required.";

        }

        if (!formData.approval_level_id) {

            validationErrors.approval_level_id =
                "Approval Level is required.";

        }

        if (Object.keys(validationErrors).length > 0) {

            setErrors(validationErrors);

            return;

        }

        try {

            setErrors({});

            if (drawerMode === "create") {

                const payload = {

                    ...formData,

                    workflow_id: workflowId,

                };

                delete payload.sequence_no;

                delete payload.display_order;

                await createWorkflowStep(payload);

            } else {

                await updateWorkflowStep(

                    selectedStep.workflow_step_id,

                    formData

                );

            }

            handleDrawerClose();

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Delete Step
     * ------------------------------------------------------------
     */

    const handleDeleteStep = async (step) => {

        if (

            !window.confirm(

                "Are you sure you want to delete this workflow step?"

            )

        ) {

            return;

        }

        try {

            await deleteWorkflowStep(

                step.workflow_step_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Move Up
     * ------------------------------------------------------------
     */

    const handleMoveUp = async (step) => {

        try {

            await moveWorkflowStepUp(

                step.workflow_step_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Move Down
     * ------------------------------------------------------------
     */

    const handleMoveDown = async (step) => {

        try {

            await moveWorkflowStepDown(

                step.workflow_step_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Activate
     * ------------------------------------------------------------
     */

    const handleActivate = async (step) => {

        try {

            await activateWorkflowStep(

                step.workflow_step_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Deactivate
     * ------------------------------------------------------------
     */

    const handleDeactivate = async (step) => {

        try {

            await deactivateWorkflowStep(

                step.workflow_step_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------
     * Render
     * ------------------------------------------------------------
     */

    return (

        <>

            <WorkflowHeader

                title="Workflow Details"

                subtitle={

                    workflow?.name ||

                    "Workflow"

                }

            />

            <div className="card shadow-sm mb-3">

                <div className="card-body">

                    <div className="row">

                        <div className="col-md-3">

                            <strong>Code</strong>

                            <br />

                            {workflow?.code}

                        </div>

                        <div className="col-md-3">

                            <strong>Name</strong>

                            <br />

                            {workflow?.name}

                        </div>

                        <div className="col-md-3">

                            <strong>Workflow Type</strong>

                            <br />

                            {workflow?.workflow_type}

                        </div>

                        <div className="col-md-3">

                            <strong>Version</strong>

                            <br />

                            {workflow?.version_no}

                        </div>

                    </div>

                </div>

            </div>

            <WorkflowStepPanel

                loading={stepLoading}

                workflowSteps={workflowSteps}

                onAdd={handleAddStep}

                onEdit={handleEditStep}

                onDelete={handleDeleteStep}

                onMoveUp={handleMoveUp}

                onMoveDown={handleMoveDown}

                onActivate={handleActivate}

                onDeactivate={handleDeactivate}

            />

            <WorkflowStepDrawer

                open={drawerOpen}

                mode={drawerMode}

                workflowStep={selectedStep}

                formData={formData}

                errors={errors}

                loading={workflowLoading}

                saving={saving}

                approvalLevels={approvalLevels}

                rejectActions={rejectActions}

                onChange={handleFormChange}

                onSubmit={handleSubmit}

                onClose={handleDrawerClose}

            />

        </>

    );

}